/**
 * EmployeePicker — ตัวเลือกพนักงานกลาง (ทุกจุดที่ต้องเลือกรายชื่อพนักงาน)
 *
 * โจทย์ลูกค้า: ไม่โชว์รายชื่อแบนทั้งบริษัท — ต้องจัดกลุ่มตามแผนก + โชว์ตำแหน่ง
 * และค้นหาได้ทั้ง แผนก / ตำแหน่ง / ชื่อ / ชื่อเล่น / รหัสพนักงาน
 *
 *   <EmployeePicker>      — เลือกคนเดียว (แทน <select> เดิม) แบบ combobox มีช่องค้นหา
 *   <EmployeeMultiPicker> — เลือกหลายคน (ใช้ใน modal มอบหมาย/ใส่กะ) ช่องค้นหา + กลุ่มแผนก
 */
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';

export interface PickerEmployee {
  emp_code: string;
  first_name?: string | null;
  last_name?: string | null;
  nickname?: string | null;
  department?: string | null;
  position?: string | null;
  status?: string | null;
}

const NO_DEPT = 'ไม่ระบุแผนก';

export function employeeDisplayName(e: PickerEmployee): string {
  const full = `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim();
  return (full || e.emp_code) + (e.nickname ? ` (${e.nickname})` : '');
}

/** ค้นหาตรงทุกช่อง: แผนก / ตำแหน่ง / ชื่อ / ชื่อเล่น / รหัสพนักงาน */
export function employeeSearchMatch(e: PickerEmployee, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [e.emp_code, e.first_name, e.last_name, e.nickname, e.department, e.position]
    .some((v) => (v ?? '').toLowerCase().includes(q));
}

export function groupEmployeesByDept(list: PickerEmployee[]): Array<{ dept: string; items: PickerEmployee[] }> {
  const byDept = new Map<string, PickerEmployee[]>();
  for (const e of list) {
    const dept = (e.department ?? '').trim() || NO_DEPT;
    if (!byDept.has(dept)) byDept.set(dept, []);
    byDept.get(dept)!.push(e);
  }
  return [...byDept.entries()]
    .sort(([a], [b]) => (a === NO_DEPT ? 1 : b === NO_DEPT ? -1 : a.localeCompare(b, 'th')))
    .map(([dept, items]) => ({ dept, items: items.sort((x, y) => x.emp_code.localeCompare(y.emp_code)) }));
}

function uniqueSorted(values: Array<string | null | undefined>): string[] {
  return [...new Set(values.map((v) => (v ?? '').trim()).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, 'th'),
  );
}

/** กรองตาม dropdown แผนก/ตำแหน่ง ('' = ทั้งหมด) */
function matchDeptPos(e: PickerEmployee, dept: string, pos: string): boolean {
  if (dept && (e.department ?? '').trim() !== dept) return false;
  if (pos && (e.position ?? '').trim() !== pos) return false;
  return true;
}

/** แถว dropdown กรอง แผนก + ตำแหน่ง (ตำแหน่งแคบลงตามแผนกที่เลือก) */
function DeptPosFilter({
  employees,
  dept,
  pos,
  onChange,
}: {
  employees: PickerEmployee[];
  dept: string;
  pos: string;
  onChange: (dept: string, pos: string) => void;
}) {
  const depts = useMemo(() => uniqueSorted(employees.map((e) => e.department)), [employees]);
  const positions = useMemo(() => {
    const pool = dept ? employees.filter((e) => (e.department ?? '').trim() === dept) : employees;
    return uniqueSorted(pool.map((e) => e.position));
  }, [employees, dept]);
  if (depts.length === 0 && positions.length === 0) return null;
  return (
    <div className="flex gap-1.5 mt-1.5">
      <select
        value={dept}
        onChange={(e) => onChange(e.target.value, '')}
        className="flex-1 min-w-0 rounded-lg border border-gray-200 px-2 py-1 text-sm text-gray-700 focus:outline-none focus:border-brand-400"
      >
        <option value="">ทุกแผนก</option>
        {depts.map((d) => <option key={d} value={d}>{d}</option>)}
      </select>
      <select
        value={pos}
        onChange={(e) => onChange(dept, e.target.value)}
        className="flex-1 min-w-0 rounded-lg border border-gray-200 px-2 py-1 text-sm text-gray-700 focus:outline-none focus:border-brand-400"
      >
        <option value="">ทุกตำแหน่ง</option>
        {positions.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
    </div>
  );
}

function RowText({ e }: { e: PickerEmployee }) {
  return (
    <div className="min-w-0 flex-1">
      <div className="text-sm text-gray-900 truncate">{employeeDisplayName(e)}</div>
      <div className="text-[11px] text-gray-400 truncate">
        {(e.position ?? '').trim() || 'ไม่ระบุตำแหน่ง'} · <span className="font-mono">{e.emp_code}</span>
      </div>
    </div>
  );
}

// === เลือกคนเดียว (combobox แทน <select>) ===
export function EmployeePicker({
  employees,
  value,
  onChange,
  placeholder = '— เลือกพนักงาน —',
  className = '',
  clearable = true,
}: {
  employees: PickerEmployee[];
  value: string | null;
  onChange: (empCode: string | null) => void;
  placeholder?: string;
  className?: string;
  clearable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [fDept, setFDept] = useState('');
  const [fPos, setFPos] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(() => employees.find((e) => e.emp_code === value) ?? null, [employees, value]);
  const groups = useMemo(
    () =>
      groupEmployeesByDept(
        employees.filter((e) => matchDeptPos(e, fDept, fPos) && employeeSearchMatch(e, query)),
      ),
    [employees, query, fDept, fPos],
  );
  const hasFilterRow = useMemo(
    () => uniqueSorted(employees.flatMap((e) => [e.department, e.position])).length > 0,
    [employees],
  );

  function pick(code: string | null) {
    onChange(code);
    setOpen(false);
    setQuery('');
    setFDept('');
    setFPos('');
  }

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); setTimeout(() => inputRef.current?.focus(), 0); }}
        className="input w-full flex items-center gap-2 text-left"
      >
        {selected ? (
          <span className="flex-1 min-w-0 truncate text-sm">
            {employeeDisplayName(selected)}
            <span className="text-gray-400"> · {(selected.position ?? '').trim() || selected.emp_code}</span>
          </span>
        ) : (
          <span className="flex-1 min-w-0 truncate text-sm text-gray-400">{placeholder}</span>
        )}
        {selected && clearable && (
          <span
            role="button"
            tabIndex={0}
            onClick={(ev) => { ev.stopPropagation(); pick(null); }}
            onKeyDown={(ev) => { if (ev.key === 'Enter') { ev.stopPropagation(); pick(null); } }}
            className="p-0.5 text-gray-300 hover:text-gray-500 shrink-0"
            aria-label="ล้างที่เลือก"
          >
            <X className="w-3.5 h-3.5" />
          </span>
        )}
        <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[70]" onClick={() => { setOpen(false); setQuery(''); setFDept(''); setFPos(''); }} />
          <div className="absolute z-[80] mt-1 left-0 right-0 bg-white rounded-xl border border-gray-200 shadow-lg max-h-80 overflow-y-auto">
            <div className="sticky top-0 z-10 bg-white p-2 border-b border-gray-100">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-300 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="ค้นหา ชื่อ / ชื่อเล่น / รหัส…"
                  className="w-full rounded-lg border border-gray-200 pl-8 pr-2 py-1.5 text-sm focus:outline-none focus:border-brand-400"
                />
              </div>
              <DeptPosFilter
                employees={employees}
                dept={fDept}
                pos={fPos}
                onChange={(d, p) => { setFDept(d); setFPos(p); }}
              />
            </div>
            {groups.length === 0 && <p className="text-center text-sm text-gray-400 py-6">ไม่พบพนักงาน</p>}
            {groups.map((g) => (
              <div key={g.dept}>
                <div className={`px-3 py-1.5 text-[11px] font-semibold text-gray-500 bg-gray-50 sticky ${hasFilterRow ? 'top-[88px]' : 'top-[52px]'}`}>
                  {g.dept} <span className="text-gray-300 font-normal">({g.items.length})</span>
                </div>
                {g.items.map((e) => (
                  <button
                    key={e.emp_code}
                    type="button"
                    onClick={() => pick(e.emp_code)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 ${e.emp_code === value ? 'bg-brand-50' : ''}`}
                  >
                    <RowText e={e} />
                  </button>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// === เลือกหลายคน (ใช้ใน modal) — ช่องค้นหา + กลุ่มแผนก + checkbox ===
export function EmployeeMultiPicker({
  employees,
  isChecked,
  onToggle,
  renderRight,
  renderExpanded,
  onToggleAll,
  selectedCount,
  maxHeightClass = 'max-h-72',
  emptyText = 'ยังไม่มีพนักงาน',
}: {
  employees: PickerEmployee[];
  isChecked: (e: PickerEmployee) => boolean;
  onToggle: (e: PickerEmployee, on: boolean) => void;
  /** เนื้อหาด้านขวาของแถว (เช่น ช่องจำนวนเงิน) */
  renderRight?: (e: PickerEmployee) => ReactNode;
  /** เนื้อหาใต้แถวเมื่อถูกเลือก (เช่น ปุ่มวันทำงาน) */
  renderExpanded?: (e: PickerEmployee) => ReactNode;
  /** ถ้าส่งมา จะโชว์แถบ "เลือกแล้ว X · เลือกทั้งหมด/ล้าง" — ได้รายการ emp_code ที่กรองอยู่ */
  onToggleAll?: (filteredCodes: string[], allSelected: boolean) => void;
  selectedCount?: number;
  maxHeightClass?: string;
  emptyText?: string;
}) {
  const [query, setQuery] = useState('');
  const [fDept, setFDept] = useState('');
  const [fPos, setFPos] = useState('');
  const filtered = useMemo(
    () => employees.filter((e) => matchDeptPos(e, fDept, fPos) && employeeSearchMatch(e, query)),
    [employees, query, fDept, fPos],
  );
  const groups = useMemo(() => groupEmployeesByDept(filtered), [filtered]);
  const allSelected = filtered.length > 0 && filtered.every((e) => isChecked(e));

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="w-4 h-4 text-gray-300 absolute left-2.5 top-1/2 -translate-y-1/2" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="ค้นหา ชื่อ / ชื่อเล่น / รหัส…"
          className="w-full rounded-lg border border-gray-200 pl-8 pr-2 py-1.5 text-sm focus:outline-none focus:border-brand-400"
        />
      </div>
      <DeptPosFilter
        employees={employees}
        dept={fDept}
        pos={fPos}
        onChange={(d, p) => { setFDept(d); setFPos(p); }}
      />

      {onToggleAll && (
        <div className="flex items-center justify-between text-xs text-gray-600">
          <span>เลือกแล้ว {selectedCount ?? 0} / {filtered.length} คน</span>
          <button
            type="button"
            onClick={() => onToggleAll(filtered.map((e) => e.emp_code), allSelected)}
            className="text-brand-600 hover:underline"
          >
            {allSelected ? 'ล้างที่เลือก' : 'เลือกทั้งหมดที่กรองอยู่'}
          </button>
        </div>
      )}

      <div className={`border border-gray-200 rounded-lg ${maxHeightClass} overflow-y-auto`}>
        {filtered.length === 0 && (
          <p className="text-center text-gray-400 py-6 text-sm">{query ? 'ไม่พบพนักงาน' : emptyText}</p>
        )}
        {groups.map((g) => (
          <div key={g.dept}>
            <div className="px-3 py-1.5 text-[11px] font-semibold text-gray-500 bg-gray-50 sticky top-0 z-10">
              {g.dept} <span className="text-gray-300 font-normal">({g.items.length})</span>
            </div>
            {g.items.map((e) => {
              const on = isChecked(e);
              return (
                <div key={e.emp_code} className={`px-3 py-1.5 border-b border-gray-50 last:border-0 ${on ? 'bg-brand-50/60' : ''}`}>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={(ev) => onToggle(e, ev.target.checked)}
                      className="w-4 h-4 shrink-0"
                    />
                    <RowText e={e} />
                    {renderRight?.(e)}
                  </div>
                  {on && renderExpanded && <div className="mt-1.5 pl-6">{renderExpanded(e)}</div>}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
